/**
 * Testi italiani dei progetti dentro un set: elenco, creazione, collegamento,
 * importazione da cartella.
 *
 * `link.noticeTitle` e `link.noticeBody` sono l'avviso che il collegamento
 * **concede permessi reali**: sta sopra il form e resta netto in ogni lingua.
 */

/** Testi dei progetti del set. */
export const setProjects = {
  /** Titolo della scheda. */
  title: 'Progetti del set',
  /** Come stanno i progetti nel set. */
  intro:
    'I progetti stanno nel volume del set, visibile a tutti i suoi agenti. Un progetto che un altro set ospita già arriva qui come clone del suo repository.',
  /** Nessun progetto. */
  empty: 'Nessun progetto nel set.',
  /** Lettura non riuscita. */
  loadFailed: 'Caricamento dei progetti del set non riuscito. Riprova.',

  /** Colonne della tabella. */
  columns: {
    /** Progetto. */
    project: 'Progetto',
    /** Modalità di lavoro. */
    workMode: 'Modalità',
    /** Origine. */
    source: 'Origine',
    /** Accesso degli agenti. */
    access: 'Accesso',
    /** Collocazione nel volume. */
    placement: 'Collocazione',
    /** Stato dell'importazione. */
    import: 'Importazione',
    /** Data di ingresso nel set. */
    linkedAt: 'Nel set dal',
  },

  /** Origine di un progetto. */
  source: {
    /** Vuoto. */
    empty: 'Vuoto',
    /** Da repository. */
    git: 'Repository',
    /** Da cartella. */
    upload: 'Cartella',
    /** Collegato. */
    link: 'Collegato',
  },

  /** Collocazione nel volume. */
  placement: {
    /** Ospitato dal set. */
    volume: 'Nel volume del set',
    /** Clonato da git. */
    clone: 'Clonato da git',
  },

  /** Stato dell'importazione. */
  import: {
    /** Niente da importare. */
    none: 'Nessuna',
    /** In attesa. */
    pending: 'In attesa',
    /** In corso. */
    importing: 'In corso',
    /** Conclusa. */
    done: 'Conclusa',
    /** Fallita. */
    failed: 'Non riuscita',
    /** File e dimensione estratti. */
    summary_one: '{{count, number}} file, {{size}}',
    /** File e dimensione estratti. */
    summary_other: '{{count, number}} file, {{size}}',
  },

  /** Fasi della creazione. */
  phase: {
    /** Compressione. */
    zipping: 'Compressione della cartella…',
    /** Creazione. */
    creating: 'Creazione del progetto…',
    /** Caricamento. */
    uploading: "Caricamento dell'archivio…",
  },

  /** Cassetto del nuovo progetto. */
  form: {
    /** Titolo del cassetto e pulsante che lo apre. */
    title: 'Nuovo progetto',
    /** Pulsante di invio. */
    submit: 'Crea progetto',
    /** Etichetta dell'origine. */
    source: 'Origine',
    /** Opzioni dell'origine. */
    sources: {
      /** Nessun progetto (procedura del primo set). */
      none: 'Nessuno per ora',
      /** Vuoto. */
      empty: 'Vuoto',
      /** Da cartella. */
      upload: 'Da cartella',
      /** Da repository. */
      git: 'Da repository',
    },
    /** Spiegazione dell'opzione «nessuno». */
    noneHelp: 'Potrai creare o importare progetti dalla scheda Progetti del set.',
    /** Etichetta del nome. */
    name: 'Nome',
    /** Etichetta dello slug. */
    slug: 'Slug',
    /** Vincoli dello slug. */
    slugHelp: 'Da 3 a 64 caratteri: minuscole, cifre e trattini. Deve iniziare con una lettera.',
    /** Etichetta della descrizione. */
    description: 'Descrizione',
    /** Etichetta della connessione. */
    connection: 'Strumento',
    /** Aiuto sotto la connessione. */
    connectionHelp: 'Facoltativo. Serve per un repository privato e perché gli agenti possano fare push.',
    /** Testo di riempimento. */
    connectionNone: 'Nessuno, repository pubblico',
    /** Opzione di una connessione. */
    connectionOption: '{{label}} ({{account}})',
    /** Nessuna connessione attiva. */
    noConnections: 'Non hai ancora collegato GitHub.',
    /** Collegamento alla pagina degli strumenti. */
    goToTools: 'Apri Strumenti',
    /** Lettura delle connessioni non riuscita. */
    connectionsFailed: 'Caricamento degli strumenti non riuscito. Riprova.',
    /** Etichetta della scelta del repository. */
    repositoryPick: 'Repository',
    /** Aiuto sotto la scelta del repository. */
    repositoryPickHelp: "Scegli dall'elenco o scrivi owner/nome di un repository nuovo.",
    /** Etichetta dell'indirizzo. */
    repositoryUrl: 'Indirizzo https del repository',
    /** Aiuto sotto l'indirizzo. */
    repositoryHelp: 'Solo https, senza utente né token nell\'indirizzo.',
    /** Etichetta dell'accesso. */
    access: 'Accesso degli agenti',
    /** Esito riuscito. */
    done: 'Progetto {{name}} creato.',
    /** Esito fallito. */
    failed: 'Creazione non riuscita. Riprova.',
    /** Rifiuto generico (422). */
    refused: 'Progetto non creato. Controlla nome, slug e indirizzo.',
    /** Slug già usato. */
    slugTaken: 'Lo slug è già usato da un altro progetto. Scegline un altro.',
    /** Rifiuto (422) di un progetto da repository con uno strumento: il collegamento non è nato. */
    refusedWithTool:
      'Progetto non creato. Controlla nome, slug e indirizzo, e che la connessione scelta veda il repository.',
    /** Creato, caricamento fallito. */
    createdUploadFailed:
      'Il progetto {{name}} è stato creato, ma il caricamento non è riuscito. Riprova con «Carica cartella» nella scheda Progetti.',
    /** Errori dei campi. */
    errors: {
      /** Nome mancante. */
      name: 'Scrivi il nome del progetto.',
      /** Slug non valido. */
      slug: 'Usa da 3 a 64 caratteri: minuscole, cifre e trattini, iniziando con una lettera.',
      /** Indirizzo non valido. */
      repositoryUrl: 'Scrivi un indirizzo https senza utente né token.',
      /** Indirizzo non di GitHub con uno strumento scelto. */
      repositoryNotGithub: 'Con uno strumento GitHub serve un indirizzo https://github.com/proprietario/nome.',
      /** Cartella mancante. */
      folder: 'Scegli la cartella da importare.',
    },
  },

  /** Modalità di lavoro del progetto (ADR 0024). */
  modes: {
    /** Etichetta della scelta. */
    label: 'Modalità',
    /** Da zero con l'AI. */
    greenfield: {
      title: "Da zero con l'AI",
      description: "Descrivi l'idea: gli agenti creano il repository, costruiscono e pubblicano il sito.",
      badge: 'Da zero',
    },
    /** Collaboratore esterno. */
    contributor: {
      title: 'Collabora a un progetto esistente',
      description: 'Gli agenti lavorano su un repository di altri e consegnano pull request. Il merge lo fa una persona.',
      badge: 'Collaboratore',
    },
    /** Affidamento totale. */
    delegated: {
      title: 'Affidaci un progetto',
      description: 'Un progetto nuovo o esistente. Gli agenti lo analizzano e scrivono solo dopo la tua approvazione.',
      badge: 'Affidato',
    },
    /** Nota della modalità da zero. */
    greenfieldNote: "Il repository nasce sull'account GitHub di institor e il sito viene pubblicato subito.",
    /** Nota del collaboratore esterno. */
    contributorNote: 'Gli agenti lavorano solo su rami institor/ e non possono fare push sul ramo principale.',
    /** Nota dell'affidamento. */
    delegatedNote: "Fino all'approvazione dell'analisi gli agenti leggono il progetto senza scriverci.",
    /** Obiettivo, per modalità. */
    brief: {
      greenfield: 'Idea del progetto',
      contributor: 'Note per gli agenti',
      delegated: 'Obiettivo',
    },
    /** Aiuto sotto l'obiettivo, per modalità. */
    briefHelp: {
      greenfield: 'Facoltativo. Gli agenti partono da qui.',
      contributor: 'Facoltativo. Per esempio le parti del codice su cui lavorare.',
      delegated: 'Che cosa devono ottenere gli agenti. Al massimo 4000 caratteri.',
    },
    /** Aiuto del collegamento obbligatorio del collaboratore esterno. */
    connectionRequiredHelp: 'Serve il tuo collegamento GitHub: con quello gli agenti aprono rami e pull request.',
    /** Componenti. */
    components: 'Componenti',
    /** Aiuto sotto i componenti. */
    componentsHelp: 'Facoltativo. Senza una scelta, gli agenti propongono lo stack.',
    /** Nomi dei componenti. */
    component: {
      auth: 'Autenticazione',
      frontend: 'Interfaccia web',
      backend: 'Backend',
      database: 'Database PostgreSQL',
      queue: 'Coda di messaggi',
    },
    /** Contenuto di partenza di un progetto affidato. */
    start: 'Contenuto di partenza',
    /** Origini di un progetto affidato. */
    starts: {
      empty: "Nessuno, solo l'obiettivo",
      upload: 'Da cartella',
      git: 'Da repository',
    },
    /** Issue del collaboratore esterno. */
    issues: 'Issue',
    /** Opzione delle issue. */
    issuesOption: 'Consenti agli agenti di aprire issue',
    mainPush: 'Ramo principale',
    mainPushOption: 'Consenti agli agenti il push sul ramo principale',
    mainPushHelp: 'Spento per default. Lo puoi cambiare anche dopo, con il livello «Autonomo» nei permessi del set.',
    /** Pulsante per rimandare il primo progetto. */
    later: 'Più tardi',
    /** Errori dei campi della modalità. */
    errors: {
      brief: "Scrivi l'obiettivo del progetto.",
      connection: 'Scegli il collegamento GitHub con cui lavorare.',
    },
  },

  /** Analisi di un progetto affidato (ADR 0024). */
  analysis: {
    /** Titolo del pannello. */
    title: 'Analisi del progetto',
    /** Stato dell'analisi. */
    state: {
      none: 'Analisi non iniziata',
      pending: 'Analisi in corso',
      ready: 'Analisi pronta',
      approved: 'Analisi approvata',
      rejected: 'Analisi rifiutata',
    },
    /** Che cosa significa ogni stato. */
    help: {
      none: 'Gli agenti partono con il primo messaggio del progetto.',
      pending:
        "Gli agenti studiano il progetto e presentano l'analisi nella conversazione. Fino all'approvazione non scrivono sul repository.",
      ready:
        "Leggi l'analisi nella conversazione. Approvata, gli agenti possono scrivere sul repository e pubblicare.",
      approved: 'Gli agenti possono scrivere sul repository e pubblicare il sito.',
      rejected: 'Il progetto resta in sola lettura. Scrivi agli agenti cosa cambiare, poi approva la nuova analisi.',
    },
    /** Pulsante di approvazione. */
    approve: 'Approva',
    /** Pulsante di rifiuto. */
    reject: 'Rifiuta',
    /** Nome accessibile dell'approvazione. */
    approveNamed: "Approva l'analisi di {{name}}",
    /** Nome accessibile del rifiuto. */
    rejectNamed: "Rifiuta l'analisi di {{name}}",
    /** Domanda della conferma. */
    confirmApprove: "Approvare l'analisi di {{name}}?",
    /** Che cosa succede. */
    confirmApproveBody: 'Gli agenti potranno scrivere sul repository, fondere e pubblicare il sito.',
    /** Domanda del rifiuto. */
    confirmReject: "Rifiutare l'analisi di {{name}}?",
    /** Che cosa succede al rifiuto. */
    confirmRejectBody: 'Il progetto resta in sola lettura finché non approvi una nuova analisi.',
    /** Esito dell'approvazione. */
    approved: 'Analisi approvata.',
    /** Esito del rifiuto. */
    rejected: 'Analisi rifiutata.',
    /** Esito fallito. */
    failed: 'Operazione non riuscita. Riprova.',
  },

  /** Cassetto del collegamento. */
  link: {
    /** Pulsante che apre il cassetto. */
    open: 'Collega progetto',
    /** Titolo del cassetto. */
    title: 'Collega progetto',
    /** Pulsante di invio. */
    submit: 'Collega progetto',
    /** Titolo dell'avviso. */
    noticeTitle: 'Il collegamento crea permessi reali',
    /** Che cosa concede. */
    noticeBody:
      'Collegare un progetto concede a ogni agente di questo set un permesso reale su quel progetto, a tuo nome.',
    /** Etichetta del progetto. */
    project: 'Progetto da collegare',
    /** Testo di riempimento. */
    projectPlaceholder: 'Scegli un progetto',
    /** Aiuto sotto il progetto. */
    projectHelp: 'Un progetto che un altro set ospita già si collega solo se ha un repository git: verrà clonato.',
    /** Progetto non scelto. */
    projectMissing: 'Scegli il progetto da collegare.',
    /** Nessun candidato. */
    noCandidates: 'Nessun progetto da collegare',
    /** Etichetta dell'accesso. */
    access: 'Accesso degli agenti',
    /** Esito riuscito. */
    done: 'Progetto collegato.',
    /** Esito fallito. */
    failed: 'Collegamento non riuscito. Riprova.',
    /** Rifiuto: ospitato altrove senza repository. */
    refused:
      'Progetto non collegato. Un altro set lo ospita e non ha un repository git: impostalo nella panoramica del progetto.',
  },

  /** Rimozione di un progetto dal set. */
  remove: {
    /** Etichetta del pulsante. */
    label: 'Togli',
    /** Nome accessibile della riga. */
    accessibleName: 'Togli {{name}} dal set',
    /** Domanda della conferma. */
    question: 'Togliere {{name}} dal set?',
    /** Che cosa succede. */
    consequence: 'I file del progetto nel volume del set vengono cancellati. Il progetto resta nella piattaforma.',
    /** Esito riuscito. */
    done: 'Progetto tolto dal set.',
    /** Esito fallito. */
    failed: 'Operazione non riuscita. Riprova.',
  },

  /** Caricamento in un progetto esistente. */
  upload: {
    /** Etichetta del pulsante. */
    label: 'Carica cartella',
    /** Nome accessibile della riga. */
    accessibleName: 'Carica una cartella in {{name}}',
    /** Titolo del cassetto. */
    title: 'Cartella per {{name}}',
    /** Pulsante di invio. */
    submit: 'Carica',
    /** Esito riuscito. */
    done: "Cartella caricata. L'estrazione parte fra poco.",
  },

  /** Scelta della cartella. */
  folder: {
    /** Etichetta del selettore. */
    label: 'Cartella del progetto',
    /** Invito al trascinamento. */
    dropHint: 'Trascina qui una cartella, oppure',
    /** Pulsante di scelta. */
    choose: 'Scegli cartella',
    /** Pulsante di cambio. */
    change: 'Cambia cartella',
    /** Titolo delle esclusioni. */
    exclusions: 'Cartelle escluse',
    /** Esclusione con il numero di file. */
    exclusionCount: '{{name}} ({{count, number}})',
    /** Toglie tutte le esclusioni. */
    includeAll: 'Includi tutto',
    /** Rimette le esclusioni predefinite. */
    restoreExclusions: 'Ripristina esclusioni',
    /** Nome della cartella. */
    folder: 'Cartella',
    /** File inclusi. */
    included: 'Inclusi',
    /** File esclusi. */
    excluded: 'Esclusi',
    /** File e dimensione. */
    filesAndSize_one: '{{count, number}} file, {{size}}',
    /** File e dimensione. */
    filesAndSize_other: '{{count, number}} file, {{size}}',
    /** Apre l'anteprima. */
    showPreview: 'Mostra i file',
    /** Chiude l'anteprima. */
    hidePreview: 'Nascondi i file',
    /** File non elencati. */
    moreFiles_one: 'e un altro file',
    /** File non elencati. */
    moreFiles_other: 'e altri {{count, number}} file',
    /** Problemi prima della compressione. */
    problem: {
      /** Nessun file. */
      empty: 'La cartella non contiene file da caricare. Controlla le esclusioni.',
      /** Troppi file. */
      tooManyFiles: 'La cartella ha più di 20.000 file. Escludi le cartelle generate e riprova.',
      /** Troppo grande. */
      tooLarge: 'La cartella supera i 500 MiB non compressi. Escludi le cartelle generate e riprova.',
    },
    /** Archivio oltre il limite. */
    tooLargeCompressed: "L'archivio compresso pesa {{size}}, oltre il limite di 50 MiB. Escludi altre cartelle e riprova.",
    /** Rifiuto del backend per dimensione. */
    refusedTooLarge: "L'archivio supera il limite di 50 MiB. Escludi altre cartelle e riprova.",
    /** Caricamento riservato agli amministratori (403 del gateway). */
    forbidden: 'Solo un amministratore della piattaforma può caricare una cartella.',
    /** Troppi caricamenti in corso, attesa non dichiarata. */
    busy: 'Troppi caricamenti in corso. Riprova fra qualche minuto.',
    /** Troppi caricamenti in corso, con l'attesa del gateway. */
    busyRetry_one: 'Troppi caricamenti in corso. Riprova fra {{count}} secondo.',
    /** Troppi caricamenti in corso, con l'attesa del gateway. */
    busyRetry_other: 'Troppi caricamenti in corso. Riprova fra {{count}} secondi.',
    /** Compressione non riuscita. */
    zipFailed: 'Compressione non riuscita. Riprova.',
    /** Caricamento non riuscito. */
    uploadFailed: 'Caricamento non riuscito. Riprova.',
  },

  /**
   * Hosting del progetto sul cluster (ADR 0010 §8). `publicTitle` e
   * `publicText` sono l'avviso che il sito è raggiungibile da chiunque: sta
   * sopra i dati dell'ambiente e resta netto in ogni lingua.
   */
  hosting: {
    /** Pulsante della riga che apre il pannello. */
    open: 'Hosting',
    /** Nome accessibile del pulsante della riga. */
    openAccessibleName: 'Hosting di {{name}}',
    /** Titolo del pannello. */
    title: 'Hosting di {{name}}',
    /** Lettura non riuscita. */
    loadFailed: "Caricamento dell'hosting non riuscito. Riprova.",
    /** Nessun ambiente. */
    none: 'Il progetto non è pubblicato.',
    /** Che cosa succede pubblicando. */
    explain:
      'Pubblicare crea per il progetto uno spazio isolato sul cluster e un indirizzo pubblico. Gli agenti dei set con il progetto in scrittura vi installano il sito e lo gestiscono. Quelli in sola lettura vedono solo stato e log.',
    /** Set in sola lettura, nessun ambiente. */
    readOnlyNone: 'Solo un set con il progetto in scrittura può pubblicarlo.',
    /** Comando di creazione. */
    publish: 'Pubblica sul cluster',
    /** Creazione avviata. */
    publishDone: 'Ambiente in preparazione.',
    /** Creazione non riuscita. */
    publishFailed: 'Pubblicazione non riuscita. Riprova.',
    /** Titolo dell'avviso sul sito pubblico. */
    publicTitle: 'Il sito è pubblico',
    /** Testo dell'avviso sul sito pubblico. */
    publicText:
      "Chiunque conosca l'indirizzo può aprirlo. I cinque caratteri casuali lo rendono solo difficile da indovinare. Non metterci dati riservati.",
    /** Set in sola lettura, ambiente presente. */
    readOnlyNotice: "Questo set ha il progetto in sola lettura: puoi consultare l'ambiente, non modificarlo.",
    /** Aggiornamento automatico in corso. */
    following: 'Lo stato si aggiorna da solo ogni 5 secondi.',
    /** Titolo dell'avviso del sito in pausa. */
    sleepingTitle: 'Sito in pausa',
    /** Che cosa succede al sito in pausa. */
    sleepingText:
      'Dopo 24 ore senza visite la demo si mette in pausa. Chi la apre vede una pagina di attesa, e la demo si riaccende da sola. La produzione resta sempre accesa.',
    /** Comando di riaccensione. */
    wake: 'Riaccendi',
    /** Riaccensione avviata. */
    wakeDone: 'Il sito si sta riaccendendo.',
    /** Riaccensione non riuscita. */
    wakeFailed: 'Riaccensione non riuscita. Riprova.',
    /** Stato del sito. */
    sleep: {
      /** Acceso. */
      awake: 'Acceso',
      /** In pausa. */
      sleeping: 'In pausa',
      /** In riaccensione. */
      waking: 'Si sta riaccendendo',
      /** Spento dal proprietario. */
      disabled: 'Spento',
    },
    /** Spegnimento per scelta del proprietario (risposta B3). */
    disable: {
      /** Comando. */
      action: 'Spegni il sito',
      /** Domanda della conferma. */
      confirmTitle: 'Spegnere il sito?',
      /** Che cosa succede. */
      confirmText: 'Chi lo apre vede «Sito non disponibile». Dati e ambiente restano, e puoi riaccenderlo quando vuoi.',
      /** Conferma. */
      confirm: 'Spegni',
      /** Fatto. */
      done: 'Sito spento.',
      /** Errore. */
      failed: 'Spegnimento non riuscito. Riprova.',
    },
    /** Sito spento dal proprietario. */
    disabledTitle: 'Sito spento',
    /** Che cosa succede al sito spento. */
    disabledText: 'Chi lo apre vede «Sito non disponibile». Le visite non lo riaccendono.',
    /** Riaccensione del sito spento. */
    enable: 'Riaccendi il sito',
    /** Riaccensione avviata. */
    enableDone: 'Il sito si sta riaccendendo.',
    /** Riaccensione non riuscita. */
    enableFailed: 'Riaccensione non riuscita. Riprova.',
    /** Nome accessibile del link al sito. */
    urlAccessibleName: '{{url}} (si apre in una nuova scheda)',
    /** Campi dell'ambiente. */
    fields: {
      /** Stato. */
      state: 'Stato',
      /** Acceso o in pausa. */
      site: 'Sito',
      /** Indirizzo del sito. */
      url: 'Indirizzo',
      /** Namespace Kubernetes. */
      namespace: 'Namespace',
      /** Certificato del sito. */
      certificate: 'Certificato',
      /** Accesso del set. */
      access: 'Accesso del set',
      /** Data di creazione. */
      createdAt: 'Creato il',
      /** Nota della piattaforma. */
      detail: 'Nota',
    },
    /** Stato dell'ambiente. */
    state: {
      /** In creazione. */
      provisioning: 'In preparazione',
      /** Pronto. */
      ready: 'Attivo',
      /** Fallito. */
      failed: 'Non riuscito',
      /** In cancellazione. */
      deleting: 'In eliminazione',
    },
    /** Stato del certificato. */
    certificate: {
      /** Emesso. */
      ready: 'Emesso',
      /** Non ancora emesso. */
      pending: 'In attesa',
    },
    /** Carichi di lavoro nel namespace. */
    workloads: {
      /** Titolo della sezione. */
      title: 'Carichi',
      /** Colonna del tipo. */
      kind: 'Tipo',
      /** Colonna del nome. */
      name: 'Nome',
      /** Colonna delle repliche pronte. */
      ready: 'Pronti',
      /** Nessun carico. */
      empty: 'Nessun carico installato. Lo installano gli agenti dei set con il progetto in scrittura.',
    },
    /** Eliminazione dell'ambiente. */
    remove: {
      /** Comando. */
      label: "Elimina l'ambiente",
      /** Domanda della conferma. */
      question: "Eliminare l'ambiente di {{name}}?",
      /** Che cosa succede. */
      consequence:
        'Il sito smette di rispondere. La piattaforma cancella lo spazio del progetto con tutto ciò che contiene: carichi, dati e volumi. Non si annulla.',
      /** Etichetta del campo di conferma. */
      confirmLabel: 'Scrivi {{hostname}} per confermare',
      /** Nome accessibile del form di conferma. */
      form: "Conferma dell'eliminazione",
      /** Eliminazione avviata. */
      done: 'Eliminazione avviata.',
      /** Eliminazione non riuscita. */
      failed: 'Eliminazione non riuscita. Riprova.',
    },
    /**
     * Spazio esistente sul cluster collegato da un amministratore (ADR 0038). Visibile
     * solo agli amministratori; il backend rifiuta gli altri con 403.
     */
    external: {
      /** Titolo della sezione di collegamento. */
      title: 'Spazio già pubblicato',
      /** Spiegazione sopra il form. */
      explain:
        'Se il progetto gira già sul cluster in uno spazio creato a mano, collegalo qui. Gli agenti del set vedono stato, eventi e log. La piattaforma non modifica né elimina nulla in quello spazio.',
      /** Nome accessibile del form. */
      form: 'Collegamento di uno spazio esistente',
      /** Etichetta del nome dello spazio. */
      namespace: 'Namespace',
      /** Aiuto sotto il nome. */
      namespaceHelp: 'Deve esistere già ed essere fra quelli ammessi dalla configurazione.',
      /** Etichetta dell'indirizzo del sito. */
      siteUrl: 'Indirizzo del sito',
      /** Aiuto sotto l'indirizzo. */
      siteUrlHelp: 'Facoltativo. Solo il nome del sito, senza percorso.',
      /** Casella della scrittura. */
      allowWrite: 'Consenti riavvii e repliche ai set con il progetto in scrittura',
      /** Titolo dell'avviso sulla scrittura. */
      writeWarningTitle: 'Il servizio è in uso',
      /** Che cosa concede la scrittura. */
      writeWarning:
        'Gli agenti possono riavviare i pod e cambiare il numero di repliche. Non modificano i carichi e non leggono le credenziali salvate nello spazio.',
      /** Seconda conferma della scrittura. */
      confirmWrite: 'Scrivi {{namespace}} per confermare la scrittura',
      /** Comando di collegamento. */
      submit: 'Collega spazio',
      /** Collegamento riuscito. */
      done: 'Spazio collegato.',
      /** Collegamento non riuscito. */
      failed: 'Collegamento non riuscito. Riprova.',
      /** Titolo dell'avviso di uno spazio collegato. */
      linkedTitle: 'Spazio collegato, non gestito dalla piattaforma',
      /** Testo dell'avviso di uno spazio collegato. */
      linkedText:
        'Il progetto è pubblicato in uno spazio creato fuori dalla piattaforma. Gli agenti vedono stato, eventi e log. La piattaforma non lo mette in pausa e non lo elimina.',
      /** Riga della scrittura nei dati. */
      write: 'Scrittura',
      /** Scrittura consentita. */
      writeOn: 'Riavvii e repliche ai set in scrittura',
      /** Scrittura non consentita. */
      writeOff: 'Non consentita',
      /** Indirizzo assente. */
      noSite: 'Non indicato',
      /** Nessun carico osservato. */
      noWorkloads: 'Nessun carico trovato nello spazio.',
      /** Comando di scollegamento. */
      unlink: 'Scollega spazio',
      /** Domanda della conferma. */
      unlinkQuestion: 'Scollegare lo spazio {{namespace}}?',
      /** Che cosa succede. */
      unlinkConsequence: "Gli agenti del set perdono subito l'accesso. Lo spazio e il sito restano come sono.",
      /** Scollegamento riuscito. */
      unlinkDone: 'Spazio scollegato.',
      /** Scollegamento non riuscito. */
      unlinkFailed: 'Scollegamento non riuscito. Riprova.',
    },
  },

  /**
   * Vetrina della landing page di institorAI (ADR 0026): domanda alla
   * creazione e interruttore nel pannello dell'hosting.
   */
  showcase: {
    /** Domanda alla creazione e nell'avviso di chi non ha ancora risposto. */
    question: 'Vuoi che questo progetto compaia sulla landing page di institorAI?',
    /** Riga di aiuto sotto la domanda. */
    help: "Si vedono solo il nome e l'indirizzo del sito pubblicato. Puoi cambiare idea in qualsiasi momento dalle impostazioni del progetto.",
    /** Scelta affermativa. */
    yes: 'Sì, mostralo',
    /** Scelta negativa. */
    no: 'No',
    /** Errori della domanda. */
    errors: {
      /** Nessuna scelta all'invio. */
      required: 'Scegli se mostrare il progetto sulla landing page.',
    },
    /** Titolo della sezione nel pannello dell'hosting. */
    title: 'Landing page di institorAI',
    /** Etichetta dell'interruttore. */
    toggle: 'Mostra sulla landing page di institorAI',
    /** Aiuto sotto l'interruttore. */
    toggleHelp: "Si vedono solo il nome e l'indirizzo del sito pubblicato.",
    /** Conferma: il progetto compare. */
    shown: 'Il progetto compare sulla landing page.',
    /** Conferma: il progetto non compare. */
    hidden: 'Il progetto non compare sulla landing page.',
    /** Modifica non riuscita. */
    failed: 'Modifica della landing page non riuscita. Riprova.',
    /** Il backend risponde 404: progetto non nel set o set di un altro. */
    notFound: 'Progetto non trovato in questo set, o non puoi cambiarne la visibilità. Ricarica la pagina.',
  },
};

/** Forma dei testi dei progetti del set. */
export type SetProjects = typeof setProjects;
